import { Rectangle } from 'pixi.js';
import type { TextMetrics } from '../metrics/TextMetrics';
import type { DynamicHeight, DynamicWidth } from '../../../../enums';
import {
    DynamicInferredDimensions,
    DynamicInferredHeight,
    DynamicInferredWidth,
    DynamicLevel,
    DynamicXAlign,
    DynamicYAlign,
} from '../../../../enums';
import { isBreakingSpace } from '../textMetricsHelpers';
import type { TextLineMetrics } from '../metrics/TextLineMetrics';
import type { HighlightSpanMetrics } from '../metrics/HighlightSpanMetrics';
import type { TextDynamicGraphicRendererSettingsBase } from './TextDynamicGraphicRenderer';
import { TextDynamicGraphicRenderer } from './TextDynamicGraphicRenderer';
import type { TextDynamicBoxRendererSettings } from './TextDynamicBoxRenderer';
import type { TextDynamicBoxBorderRendererSettings } from './TextDynamicBoxBorderRenderer';

export interface TextDynamicBoxBaseRendererSettings extends TextDynamicGraphicRendererSettingsBase {
    boxHeight: DynamicHeight;
    boxWidth: DynamicWidth;
}

export type Padding = {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

type HorizontalCalculation = {
    levelBounds: Rectangle;
    maxWidthBounds: Rectangle;
    previousBounds?: Rectangle;
};

type VerticalCalculation = {
    levelBounds: Rectangle;
    maxHeightBounds: Rectangle;
    baselineY: number;
    maxAscent: number;
    maxDescent: number;
};

/**
 * Renders a rounded rectangle behind a block of text.
 */
export abstract class TextDynamicBoxBaseRenderer extends TextDynamicGraphicRenderer {
    boxHeight: DynamicHeight = DynamicInferredHeight.height;
    boxWidth: DynamicWidth = DynamicInferredWidth.width;

    /**
     * This contains the bounds representation for each shape that this graphic will render. The bounds here
     * result from the level property. An object level graphic will always just have one bounds, but a line
     * level graphic will have one bounds for each line of text, etc. These bounds represent what will be drawn
     * and have their origin relative to the top left corner of the text block.
     */
    protected _levelBounds!: Rectangle[];

    protected abstract _getPadding(): Padding;
    protected abstract _getLineWidth(): number;

    #applyPaddingToBounds(levelBounds: Rectangle[]) {
        const padding = this._getPadding();
        levelBounds.forEach((bounds) => {
            applyPaddingToRectangle(bounds, padding);
        });
    }

    override apply(
        settings: TextDynamicBoxRendererSettings | TextDynamicBoxBorderRendererSettings,
    ) {
        super.apply(settings);
        this.boxHeight = settings.boxHeight;
        this.boxWidth = settings.boxWidth;
    }

    /**
     * Builds up a collection of bounds, one for each level element. Once the bounds are generated we can use
     * them to draw the individual shapes and calculate the combined bounds.
     */
    override build(metrics: TextMetrics) {
        super.build(metrics);
        let levelBounds: Rectangle[];

        switch (this.level) {
            case DynamicLevel.object:
                levelBounds = this.#getObjectBounds(metrics);
                break;
            case DynamicLevel.line:
                levelBounds = this.#getLineBounds(metrics);
                break;
            case DynamicLevel.word:
                levelBounds = this.#getWordBounds(metrics);
                break;
            case DynamicLevel.highlight:
                levelBounds = this.#getHighlightBounds(metrics);
                break;
            default:
                throw new Error(`Invalid level ${this.level}`);
        }

        this.#applyPaddingToBounds(levelBounds);
        this._levelBounds = levelBounds;
    }

    #getObjectBounds(metrics: TextMetrics) {
        const { x, width } = this.#getWidthAndX({
            levelBounds: metrics.bounds,
            maxWidthBounds: metrics.bounds,
        });
        const { y, height } = this.#getHeightAndY({
            levelBounds: metrics.bounds,
            maxHeightBounds: metrics.bounds,
            baselineY: metrics.baselineY,
            maxAscent: metrics.maxLineAscent,
            maxDescent: metrics.maxLineDescent,
        });
        const rectangle = new Rectangle(x, y, width, height);
        return [rectangle];
    }

    #getLineBounds(metrics: TextMetrics) {
        const bounds: Rectangle[] = [];
        metrics.lines.forEach((line, index) => {
            const { x, width } = this.#getWidthAndX({
                levelBounds: line.bounds,
                maxWidthBounds: metrics.bounds,
            });
            const { y, height } = (() => {
                if (this.boxHeight === DynamicInferredHeight.gapless) {
                    return this.#getGaplessHeightForLine(
                        metrics.lines,
                        index,
                        metrics.maxLineHeight,
                    );
                }
                return this.#getHeightAndY({
                    levelBounds: line.bounds,
                    maxHeightBounds: line.bounds,
                    baselineY: line.baselineY,
                    maxAscent: metrics.maxLineAscent,
                    maxDescent: metrics.maxLineDescent,
                });
            })();

            const rectangle = new Rectangle(x, y, width, height);
            bounds.push(rectangle);
        });
        return bounds;
    }

    #getWordBounds(metrics: TextMetrics) {
        const bounds: Rectangle[] = [];
        metrics.lines.forEach((line, index) => {
            line.words.forEach((word) => {
                if (isBreakingSpace(word.text)) {
                    return;
                }

                // Also use the word for max width bounds, because we want to limit the graphic on each word to the word width
                const { x, width } = this.#getWidthAndX({
                    levelBounds: word.bounds,
                    maxWidthBounds: word.bounds,
                    previousBounds: bounds.at(-1),
                });

                const { y, height } = (() => {
                    if (this.boxHeight === DynamicInferredHeight.gapless) {
                        return this.#getGaplessHeightForLine(
                            metrics.lines,
                            index,
                            metrics.maxLineHeight,
                        );
                    }
                    return this.#getHeightAndY({
                        levelBounds: word.bounds,
                        maxHeightBounds: line.bounds,
                        baselineY: line.baselineY,
                        maxAscent: metrics.maxLineAscent,
                        maxDescent: metrics.maxLineDescent,
                    });
                })();

                const rectangle = new Rectangle(x, y, width, height);
                bounds.push(rectangle);
            });
        });

        return bounds;
    }

    #getHighlightBounds(metrics: TextMetrics) {
        const bounds: Rectangle[] = [];
        metrics.lines.forEach((line, index) => {
            line.highlightSpans.forEach((span) => {
                // Also use the span for max width bounds, because we want to limit the graphic on each span to the span width
                const { x, width } = this.#getWidthAndX({
                    levelBounds: span.bounds,
                    maxWidthBounds: span.bounds,
                    previousBounds: bounds.at(-1),
                });
                const { y, height } = (() => {
                    if (this.boxHeight === DynamicInferredHeight.gapless) {
                        return this.#getGaplessHeightForLine(
                            metrics.lines,
                            index,
                            metrics.maxLineHeight,
                        );
                    }
                    return this.#getHeightAndY({
                        levelBounds: span.bounds,
                        maxHeightBounds: line.bounds,
                        baselineY: line.baselineY,
                        maxAscent: metrics.maxHighlightSpanAscent,
                        maxDescent: metrics.maxHighlightSpanDescent,
                    });
                })();

                const rectangle = new Rectangle(x, y, width, height);
                bounds.push(rectangle);
            });
        });

        return bounds;
    }

    #getWidthAndX({ levelBounds, maxWidthBounds, previousBounds }: HorizontalCalculation): {
        x: number;
        width: number;
    } {
        let x: number, width: number;
        if (typeof this.boxWidth === 'number') {
            width = this.boxWidth;
            switch (this.alignX) {
                case DynamicXAlign.left:
                    x = levelBounds.x;
                    break;
                case DynamicXAlign.center:
                    x = levelBounds.x + levelBounds.width * 0.5 - this.boxWidth * 0.5;
                    break;
                case DynamicXAlign.right:
                    x = levelBounds.x + levelBounds.width - this.boxWidth;
                    break;
                default:
                    throw new Error(`Invalid alignX ${this.alignX}`);
            }
        } else {
            switch (this.boxWidth) {
                case DynamicInferredWidth.width:
                    x = levelBounds.x;
                    width = levelBounds.width;
                    break;
                case DynamicInferredWidth.maxWidth:
                    x = maxWidthBounds.x;
                    width = maxWidthBounds.width;
                    break;
                case DynamicInferredWidth.gaplessWidth:
                    previousBounds = previousBounds ?? levelBounds;
                    const gap = this.#getGapToPrecedingSibling(previousBounds, levelBounds);
                    x = levelBounds.x - gap;
                    width = levelBounds.width + gap;
                    break;
                default:
                    throw new Error(`Invalid inferred box width ${this.boxWidth}`);
            }
        }
        return {
            x: x + this.positionX,
            width,
        };
    }

    #getGapToPrecedingSibling(precedingBounds: Rectangle, levelBounds: Rectangle): number {
        if (precedingBounds.x + precedingBounds.width > levelBounds.x) {
            // The previous sibling finishes after the current level starts, so there is no gap to fill
            // Likely this means the previous sibling is on the previous line
            return 0;
        }
        const padding = this._getPadding();
        const precedingEdge = precedingBounds.x + precedingBounds.width + padding.right;
        const currentEdge = levelBounds.x - padding.left;
        return currentEdge - precedingEdge;
    }

    #getHeightAndY({
        levelBounds,
        maxHeightBounds,
        baselineY,
        maxAscent,
        maxDescent,
    }: VerticalCalculation): { y: number; height: number } {
        let y: number, height: number;
        if (typeof this.boxHeight === 'number') {
            height = this.boxHeight;
            switch (this.alignY) {
                case DynamicYAlign.maxTop:
                    y = maxHeightBounds.y;
                    break;
                case DynamicYAlign.top:
                    y = levelBounds.y;
                    break;
                case DynamicYAlign.center:
                    y = levelBounds.y + levelBounds.height * 0.5 - this.boxHeight * 0.5;
                    break;
                case DynamicYAlign.averageCenter:
                    y = maxHeightBounds.y + maxHeightBounds.height * 0.5 - this.boxHeight * 0.5;
                    break;
                case DynamicYAlign.baseline:
                    y = baselineY - this.boxHeight;
                    break;
                case DynamicYAlign.bottom:
                    y = levelBounds.y + levelBounds.height - this.boxHeight;
                    break;
                case DynamicYAlign.maxBottom:
                    y = maxHeightBounds.y + maxHeightBounds.height - this.boxHeight;
                    break;
                default:
                    throw new Error(`Invalid alignY ${this.alignY}`);
            }
        } else if (this.level === DynamicLevel.object) {
            y = levelBounds.y;
            height = levelBounds.height;
        } else {
            const maxHeight = maxAscent + maxDescent;

            switch (this.boxHeight) {
                case DynamicInferredHeight.gapless:
                case DynamicInferredHeight.height:
                    y = levelBounds.y;
                    height = levelBounds.height;
                    break;
                case DynamicInferredHeight.maxHeight:
                    y = baselineY - maxAscent;
                    height = maxHeight;
                    break;
                case DynamicInferredHeight.heightWithoutDescent:
                    y = baselineY - maxAscent;
                    height = maxAscent;
                    break;
                default:
                    throw new Error(`Invalid inferred box height ${this.boxHeight}`);
            }
        }

        return {
            y: y + this.positionY,
            height,
        };
    }

    /**
     * Calculates the vertical position and height needed to create "gapless" word graphics.
     *
     * This works by extending the box graphic on a given line to fill the gap above or below that line if the line
     * above or below it is wider than the given line. Additionally, for the gaps where the box is extended, we add the line
     * width so that the graphics overlap seamlessly, instead of being flush against each other.
     */
    #getGaplessHeightForLine(
        lines: TextLineMetrics[] | HighlightSpanMetrics[],
        index: number,
        maxLineHeight: number,
    ): { y: number; height: number } {
        const line = lines[index];
        const lineBounds = line.bounds;

        const padding = this._getPadding();
        const getLineTopY = (bounds: Rectangle) => bounds.y - padding.top;
        const getLineBottomY = (bounds: Rectangle) => bounds.y + maxLineHeight + padding.bottom;
        let topGap = 0;
        let bottomGap = 0;

        // Find vertical gap/overlap with the previous line if the previous line is wider or equal
        if (index > 0) {
            const previousTextLine = lines[index - 1];
            const previousLineBounds = previousTextLine.bounds;

            if (
                previousTextLine.visibleWords.length > 0 &&
                previousLineBounds.width >= lineBounds.width
            ) {
                topGap = getLineTopY(lineBounds) - getLineBottomY(previousLineBounds);
                topGap += this._getLineWidth();
            }
        }

        // Find vertical gap/overlap with the next line if the next line is wider
        if (index < lines.length - 1) {
            const nextTextLine = lines[index + 1];
            const nextLineBounds = nextTextLine.bounds;
            if (nextTextLine.visibleWords.length > 0 && nextLineBounds.width > lineBounds.width) {
                bottomGap = getLineTopY(nextLineBounds) - getLineBottomY(lineBounds);
                bottomGap += this._getLineWidth();
            }
        } else {
            // We don't want the last line to have excessive space below it, but the overall height (line with gaps)
            // should still be at least the max line height to keep a more consistent visual appearance.
            const heightWithLineHeight = lineBounds.height + topGap;
            if (heightWithLineHeight > maxLineHeight) {
                bottomGap = lineBounds.height - maxLineHeight;
            }
        }

        return {
            y: lineBounds.y - topGap,
            height: maxLineHeight + topGap + bottomGap,
        };
    }

    override _getInferredValues(inferredDimension: DynamicInferredDimensions): number[] {
        switch (inferredDimension) {
            case DynamicInferredDimensions.height:
                return this._levelBounds.map((bounds) => bounds.height);
            case DynamicInferredDimensions.maxHeight:
                const maxHeight = Math.max(...this._levelBounds.map((bounds) => bounds.height));
                return Array(this._levelBounds.length).fill(maxHeight);
            case DynamicInferredDimensions.width:
                return this._levelBounds.map((bounds) => bounds.width);
            case DynamicInferredDimensions.maxWidth:
                const maxWidth = Math.max(...this._levelBounds.map((bounds) => bounds.width));
                return Array(this._levelBounds.length).fill(maxWidth);

            // The rest are not valid inferred dimensions when animating a graphic
            case DynamicInferredDimensions.gapless:
            case DynamicInferredDimensions.heightWithoutDescent:
            case DynamicInferredDimensions.gaplessWidth:
            default:
                throw new Error(`Invalid inferred dimension ${inferredDimension}`);
        }
    }
}

/**
 * Applies padding to a rectangle by updating the passed rectangle in place.
 */
const applyPaddingToRectangle = (rectangle: Rectangle, padding: Padding) => {
    rectangle.x -= padding.left;
    rectangle.y -= padding.top;
    rectangle.width += padding.left + padding.right;
    rectangle.height += padding.top + padding.bottom;
};
